import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { GuestAuthDialog } from '@/auth/components/guest-auth-dialog';
import { useAuth } from '@/auth/use-auth';
import {
  useConversationList,
  useDeleteConversation,
  useRenameConversation,
  type ChatConversation,
} from '@/features/chat';
import { GemBadge, gemName, useGems } from '@/features/gems';
import { SettingsDialog } from '@/features/settings';
import { paths } from '@/routes/paths';
import { Iconify } from '@/shared/components/iconify';
import { PlusIcon, SearchIcon } from '@/shared/components/icons';
import { Logo, LOGO_SIZE } from '@/shared/components/logo';
import { useColorScheme } from '@/theme';

// Sidebar chrome for the chat surface. Lives in `layouts/` so the page layer
// only renders message content + composer.
// Mirrors `sahib/project/pages/chat.jsx::Sidebar` from the design bundle.

export const SIDEBAR_WIDTH = 280;
// Mini-collapsed width — icon-rail only, matches v1's `--layout-nav-mini-width`
// pattern. Wide enough for a 40px icon button with 16px horizontal padding.
export const SIDEBAR_MINI_WIDTH = 72;
export const GENERAL_GEM_ACCENT = '#6B8E6F';
export const GENERAL_GEM_ID = 'general';

// Design tokens. The sidebar is a slightly darker, warmer surface than the chat
// area — distinct from any palette key, so hardcoded with mode-aware switching
// per `sahib/project/pages/chat.jsx::T`.
const SIDEBAR_BG_LIGHT = '#F4F2EC';
const SIDEBAR_BG_DARK = '#0A0C0B';

interface ChatSidebarProps {
  /** Drawer open state (mobile only — ignored on desktop where the sidebar is always visible). */
  open: boolean;
  /**
   * `persistent` — in-flow push panel (desktop default).
   * `temporary` — MUI `<Drawer>` overlay on mobile; backdrop + focus trap +
   *   Escape + scroll lock come from MUI for free.
   */
  variant?: 'persistent' | 'temporary';
  /** Desktop only — when true, collapse to an icon-only rail (~72px). */
  mini?: boolean;
  /** Toggle action wired to the in-sidebar control. */
  onToggle: () => void;
  /** Invoked when the user closes the drawer (backdrop, Escape, swipe). */
  onClose?: () => void;
  activeGemId: string;
  onPickGem: (id: string) => void;
  activeConversationId?: string;
  onPickConversation: (id: string) => void;
  onNewConversation: () => void;
}

export function ChatSidebar({
  open,
  variant = 'persistent',
  mini = false,
  onToggle,
  onClose,
  activeGemId,
  onPickGem,
  activeConversationId,
  onPickConversation,
  onNewConversation,
}: ChatSidebarProps) {
  const isTemporary = variant === 'temporary';
  const isMini = !isTemporary && mini;
  const { t, i18n } = useTranslation('chat');
  const { user, isGuest, logout } = useAuth();
  const { mode, toggle: toggleColorMode } = useColorScheme();
  const [gemsExpanded, setGemsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Guests get a "Log in" entry (instead of "Sign out") that opens this dialog
  // to sign into an existing account or upgrade the guest session to a real one.
  const [guestAuthOpen, setGuestAuthOpen] = useState(false);
  // Per-conversation 3-dot menu and the delete-confirmation dialog.
  const [historyMenu, setHistoryMenu] = useState<{
    anchor: HTMLElement;
    id: string;
    title: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const deleteMutation = useDeleteConversation();
  const renameMutation = useRenameConversation();

  const closeMenu = () => setMenuAnchor(null);

  // History scrollbar: the thin track-line is invisible until the user scrolls,
  // then fades back out ~700ms after scrolling stops.
  const [historyScrolling, setHistoryScrolling] = useState(false);
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleHistoryScroll = () => {
    setHistoryScrolling(true);
    if (scrollIdleTimer.current) clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = setTimeout(() => setHistoryScrolling(false), 700);
  };
  useEffect(() => () => clearTimeout(scrollIdleTimer.current), []);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setDeleteTarget(null);
        // The open conversation was just removed — fall back to a fresh chat.
        if (id === activeConversationId) onNewConversation();
      },
    });
  };

  const handleConfirmRename = () => {
    if (!renameTarget) return;
    const next = renameValue.trim();
    // Nothing to do for an empty or unchanged name — just close.
    if (!next || next === renameTarget.title) {
      setRenameTarget(null);
      return;
    }
    renameMutation.mutate(
      { id: renameTarget.id, title: next },
      { onSuccess: () => setRenameTarget(null) },
    );
  };
  const nextLang = i18n.language?.startsWith('ar') ? 'en' : 'ar';
  const nextLangLabel = nextLang === 'ar' ? 'العربية' : 'English';
  const { data: conversationData } = useConversationList();
  const conversations = conversationData?.conversations ?? [];

  const { data: gems = [] } = useGems();
  const visibleGems = gemsExpanded ? gems : gems.slice(0, 1);
  const q = searchQuery.trim().toLowerCase();
  const filteredConversations = q
    ? conversations.filter((c) => c.title.toLowerCase().includes(q))
    : conversations;
  const groupedHistory = HISTORY_GROUPS.reduce<Record<HistoryGroup, ChatConversation[]>>(
    (acc, g) => {
      acc[g] = filteredConversations.filter(
        (c) => bucketForTimestamp(c.lastMessageAt ?? c.updatedAt) === g,
      );
      return acc;
    },
    { today: [], yesterday: [], lastWeek: [], earlier: [] },
  );

  // Full sidebar (280px) — used by Drawer on mobile, and by the persistent
  // push panel on desktop when not in mini-collapsed mode.
  const fullContent = (
      <Box sx={{ width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}
        >
          <Box
            component={RouterLink}
            to={paths.home}
            aria-label={t('sidebar.homeAria', { defaultValue: 'Go to home' })}
            sx={{
              display: 'inline-flex',
              color: 'inherit',
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            <Logo size={LOGO_SIZE.primary} />
          </Box>
          {/* Desktop-only — flips between vertical and mini. On mobile the drawer
              is opened/closed from the topbar MenuButton, so this in-sidebar
              toggle is omitted to avoid a redundant control. */}
          {!isTemporary && (
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label={t('sidebar.toggleAria')}
              sx={{ color: 'text.secondary' }}
            >
              <Iconify icon="custom:sidebar-panel-outline" width={18} />
            </IconButton>
          )}
        </Stack>

        <Box sx={{ px: 2.25, pb: 1.5 }}>
          <Button
            fullWidth
            onClick={onNewConversation}
            startIcon={<PlusIcon size={14} />}
            sx={{
              justifyContent: 'flex-start',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              py: 1.1,
              px: 2,
              fontWeight: 500,
              fontSize: 13.5,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            {t('sidebar.newConversation')}
          </Button>
        </Box>

        <Box sx={{ px: 2.25, pb: 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                insetInlineStart: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.disabled',
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <SearchIcon size={14} />
            </Box>
            <Box
              component="input"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              placeholder={t('sidebar.search')}
              aria-label={t('sidebar.search')}
              sx={(theme) => ({
                width: '100%',
                paddingBlock: '8px',
                paddingInlineStart: '34px',
                paddingInlineEnd: '12px',
                fontSize: 13,
                fontFamily: 'inherit',
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                border: 1,
                borderStyle: 'solid',
                borderColor: theme.palette.divider,
                borderRadius: `${theme.shape.borderRadius}px`,
                outline: 'none',
                '&::placeholder': { color: theme.palette.text.disabled },
                '&:focus': { borderColor: theme.palette.text.disabled },
              })}
            />
          </Box>
        </Box>

        <Box sx={{ px: 1.25, pb: 1.5 }}>
          <Box sx={{ px: 1.25, py: 0.75 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'text.disabled',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {t('sidebar.gemsHeading')}
            </Typography>
          </Box>
          <Stack spacing={0.25}>
            <GemRow
              name={t('sidebar.generalGem')}
              glyph={<Iconify icon="eva:star-fill" width={12} />}
              accent={GENERAL_GEM_ACCENT}
              active={activeGemId === GENERAL_GEM_ID}
              onClick={() => onPickGem(GENERAL_GEM_ID)}
            />
            {visibleGems.map((g) => (
              <GemRow
                key={g.id}
                name={gemName(g, i18n.language)}
                accent={g.accent}
                active={activeGemId === g.slug}
                onClick={() => onPickGem(g.slug)}
              />
            ))}
            {gems.length > 1 && (
              <Box
                component="button"
                type="button"
                onClick={() => setGemsExpanded((v) => !v)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  width: '100%',
                  px: 1.25,
                  py: 0.75,
                  mt: 0.25,
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
                  })}
                >
                  <Iconify
                    icon="eva:arrow-ios-forward-fill"
                    width={14}
                    sx={{
                      transform: gemsExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary' }}>
                  {gemsExpanded
                    ? t('sidebar.showLess')
                    : t('sidebar.browseAllGems', { count: gems.length + 1 })}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box sx={{ borderTop: 1, borderTopColor: 'divider', mx: 2.25, my: 0.5 }} />

        <Box
          onScroll={handleHistoryScroll}
          sx={(theme) => {
            const thumb =
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.22)';
            return {
              flex: 1,
              overflowY: 'auto',
              px: 1.25,
              pb: 2,
              pt: 1,
              // Firefox — thin overlay scrollbar, no arrows.
              scrollbarWidth: 'thin',
              scrollbarColor: historyScrolling ? `${thumb} transparent` : 'transparent transparent',
              transition: 'scrollbar-color 0.3s',
              // WebKit — thin track, arrows hidden, thumb fades with scroll.
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                backgroundColor: historyScrolling ? thumb : 'transparent',
                transition: 'background-color 0.3s',
              },
            };
          }}
        >
          {filteredConversations.length === 0 && (
            <Typography
              sx={{
                px: 1.25,
                py: 2,
                fontSize: 12,
                color: 'text.disabled',
                lineHeight: 1.5,
              }}
            >
              {t('sidebar.noConversations')}
            </Typography>
          )}
          {HISTORY_GROUPS.map((group) => {
            const items = groupedHistory[group];
            if (items.length === 0) return null;
            return (
              <Box key={group} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'text.disabled',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {t(`sidebar.historyGroups.${group}`)}
                </Typography>
                {items.map((h) => {
                  const isActive = h.id === activeConversationId;
                  const menuOpen = historyMenu?.id === h.id;
                  return (
                    <Box
                      key={h.id}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: (t) => `${t.shape.borderRadius}px`,
                        backgroundColor: isActive ? 'action.selected' : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive ? 'action.selected' : 'action.hover',
                        },
                        '&:hover .chat-item-menu-btn': { opacity: 1 },
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => onPickConversation(h.id)}
                        sx={{
                          all: 'unset',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            minWidth: 0,
                            fontSize: 13,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? 'text.primary' : 'text.secondary',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            textAlign: 'start',
                          }}
                        >
                          {h.title}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        className="chat-item-menu-btn"
                        onClick={(e) =>
                          setHistoryMenu({ anchor: e.currentTarget, id: h.id, title: h.title })
                        }
                        aria-label={t('sidebar.historyItem.menu')}
                        aria-haspopup="menu"
                        sx={{
                          flexShrink: 0,
                          marginInlineEnd: 0.5,
                          color: 'text.disabled',
                          opacity: isActive || menuOpen ? 1 : 0,
                          transition: 'opacity 0.15s',
                          '&:hover': { color: 'text.primary' },
                        }}
                      >
                        <Iconify icon="eva:more-vertical-fill" width={15} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ borderTop: 1, borderTopColor: 'divider', p: 1.5 }}>
          {/* The whole row is the menu trigger — clicking anywhere (avatar, name,
              or the 3-dot glyph) opens the popup. The 3-dot is a plain icon now,
              not a nested button, since a <button> can't contain a <button>. */}
          <Stack
            component="button"
            type="button"
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label={t('sidebar.userFooter.menu')}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            sx={{
              appearance: 'none',
              border: 'none',
              font: 'inherit',
              color: 'inherit',
              textAlign: 'start',
              cursor: 'pointer',
              boxSizing: 'border-box',
              width: '100%',
              p: 1,
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              backgroundColor: 'transparent',
              transition: 'background-color 0.15s',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: `${GENERAL_GEM_ACCENT}33`,
                color: GENERAL_GEM_ACCENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontFamily: '"Cairo Variable", "Cairo", sans-serif',
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              {user ? getInitials(user.displayName) : 'SH'}
            </Box>
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: 500,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'start',
              }}
            >
              {user?.displayName ?? 'Sahib user'}
            </Typography>
            <Box
              component="span"
              sx={{ display: 'inline-flex', color: 'text.disabled', flexShrink: 0 }}
            >
              <Iconify icon="eva:more-vertical-fill" width={16} />
            </Box>
          </Stack>
        </Box>
      </Box>
  );

  // Mini sidebar (72px) — icon-rail. Desktop-only. Hides search, conversation
  // history, gem labels, and user-name label; keeps logo, toggle, new chat,
  // gem badges (tooltip on hover), and the user-avatar menu entry-point.
  const miniContent = (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{ width: SIDEBAR_MINI_WIDTH, height: '100%', py: 2 }}
    >
      <Box
        component={RouterLink}
        to={paths.home}
        aria-label={t('sidebar.homeAria', { defaultValue: 'Go to home' })}
        sx={{ display: 'inline-flex', color: 'inherit' }}
      >
        <Logo size={LOGO_SIZE.compact} />
      </Box>
      <Tooltip title={t('sidebar.toggleAria')} placement="right">
        <IconButton size="small" onClick={onToggle} aria-label={t('sidebar.toggleAria')}>
          <Iconify icon="custom:sidebar-panel-outline" width={20} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('sidebar.newConversation')} placement="right">
        <IconButton onClick={onNewConversation} aria-label={t('sidebar.newConversation')}>
          <PlusIcon size={18} />
        </IconButton>
      </Tooltip>
      <Box sx={{ width: 32, borderTop: 1, borderTopColor: 'divider', my: 0.5 }} />
      <Tooltip title={t('sidebar.generalGem')} placement="right">
        <Box
          component="button"
          type="button"
          onClick={() => onPickGem(GENERAL_GEM_ID)}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            borderRadius: '50%',
            outline:
              activeGemId === GENERAL_GEM_ID ? `2px solid ${GENERAL_GEM_ACCENT}` : 'none',
            outlineOffset: 2,
          }}
        >
          <GemBadge
            name={t('sidebar.generalGem')}
            accent={GENERAL_GEM_ACCENT}
            active={activeGemId === GENERAL_GEM_ID}
            glyph={<Iconify icon="eva:star-fill" width={12} />}
          />
        </Box>
      </Tooltip>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          // Reuse the same fade-on-idle scrollbar treatment as the full content.
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
        }}
      >
        {gems.map((g) => {
          const name = gemName(g, i18n.language);
          const isActive = activeGemId === g.slug;
          return (
            <Tooltip key={g.id} title={name} placement="right">
              <Box
                component="button"
                type="button"
                onClick={() => onPickGem(g.slug)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  borderRadius: '50%',
                  outline: isActive ? `2px solid ${g.accent}` : 'none',
                  outlineOffset: 2,
                }}
              >
                <GemBadge name={name} accent={g.accent} active={isActive} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ width: 32, borderTop: 1, borderTopColor: 'divider', my: 0.5 }} />
      <Tooltip title={user?.displayName ?? 'Sahib user'} placement="right">
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label={t('sidebar.userFooter.menu')}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          sx={{
            width: 36,
            height: 36,
            backgroundColor: `${GENERAL_GEM_ACCENT}33`,
            color: GENERAL_GEM_ACCENT,
            fontWeight: 700,
            fontSize: 12,
            fontFamily: '"Cairo Variable", "Cairo", sans-serif',
            '&:hover': { backgroundColor: `${GENERAL_GEM_ACCENT}4D` },
          }}
        >
          {user ? getInitials(user.displayName) : 'SH'}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      {isTemporary ? (
        <Drawer
          open={open}
          onClose={onClose}
          anchor={i18n.dir() === 'rtl' ? 'right' : 'left'}
          slotProps={{
            paper: {
              sx: (theme) => ({
                width: SIDEBAR_WIDTH,
                backgroundColor:
                  theme.palette.mode === 'dark' ? SIDEBAR_BG_DARK : SIDEBAR_BG_LIGHT,
                borderInlineEnd: 1,
                borderInlineEndColor: 'divider',
                overflow: 'hidden',
                backgroundImage: 'none',
              }),
            },
          }}
        >
          {fullContent}
        </Drawer>
      ) : (
        <Box
          component="aside"
          sx={(theme) => ({
            width: isMini ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
            flexShrink: 0,
            backgroundColor:
              theme.palette.mode === 'dark' ? SIDEBAR_BG_DARK : SIDEBAR_BG_LIGHT,
            borderInlineEnd: 1,
            borderInlineEndColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
          })}
        >
          {isMini ? miniContent : fullContent}
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { borderRadius: (t) => `${t.shape.borderRadius}px`, minWidth: 208, mb: 1 } },
        }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setSettingsOpen(true);
          }}
          sx={{ fontSize: 13.5 }}
        >
          <ListItemIcon>
            <Iconify icon="solar:settings-bold-duotone" width={18} />
          </ListItemIcon>
          {t('sidebar.userFooter.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleColorMode();
            closeMenu();
          }}
          sx={{ fontSize: 13.5 }}
        >
          <ListItemIcon>
            <Iconify
              icon={mode === 'dark' ? 'custom:sun-outline' : 'custom:moon-outline'}
              width={18}
            />
          </ListItemIcon>
          {mode === 'dark' ? t('sidebar.userFooter.lightMode') : t('sidebar.userFooter.darkMode')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            void i18n.changeLanguage(nextLang);
            closeMenu();
          }}
          sx={{ fontSize: 13.5 }}
        >
          <ListItemIcon>
            <Iconify icon="custom:globe-outline" width={18} />
          </ListItemIcon>
          {nextLangLabel}
        </MenuItem>
        <Divider />
        {isGuest ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              setGuestAuthOpen(true);
            }}
            sx={{ fontSize: 13.5 }}
          >
            <ListItemIcon>
              <Iconify icon="custom:login-outline" width={18} />
            </ListItemIcon>
            {t('sidebar.userFooter.login')}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              closeMenu();
              void logout();
            }}
            sx={{ fontSize: 13.5, color: 'error.main' }}
          >
            <ListItemIcon>
              <Iconify icon="custom:logout-outline" width={18} sx={{ color: 'error.main' }} />
            </ListItemIcon>
            {t('sidebar.userFooter.logout')}
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={historyMenu?.anchor ?? null}
        open={Boolean(historyMenu)}
        onClose={() => setHistoryMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { borderRadius: (t) => `${t.shape.borderRadius}px`, minWidth: 168 } },
        }}
      >
        <MenuItem
          onClick={() => {
            if (historyMenu) {
              setRenameTarget({ id: historyMenu.id, title: historyMenu.title });
              setRenameValue(historyMenu.title);
            }
            setHistoryMenu(null);
          }}
          sx={{ fontSize: 13.5 }}
        >
          <ListItemIcon>
            <Iconify icon="custom:edit-outline" width={18} />
          </ListItemIcon>
          {t('sidebar.historyItem.rename')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (historyMenu) setDeleteTarget({ id: historyMenu.id, title: historyMenu.title });
            setHistoryMenu(null);
          }}
          sx={{ fontSize: 13.5, color: 'error.main' }}
        >
          <ListItemIcon>
            <Iconify icon="custom:trash-outline" width={18} sx={{ color: 'error.main' }} />
          </ListItemIcon>
          {t('sidebar.historyItem.delete')}
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteTarget(null);
        }}
        slotProps={{
          paper: { sx: { borderRadius: (t) => `${t.shape.borderRadius}px`, maxWidth: 380 } },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          {t('sidebar.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {t('sidebar.deleteDialog.body', { title: deleteTarget?.title ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteMutation.isPending}
            sx={{ color: 'text.secondary' }}
          >
            {t('sidebar.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            variant="contained"
            color="error"
          >
            {t('sidebar.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(renameTarget)}
        onClose={() => {
          if (!renameMutation.isPending) setRenameTarget(null);
        }}
        slotProps={{
          paper: {
            sx: { borderRadius: (t) => `${t.shape.borderRadius}px`, maxWidth: 420, width: '100%' },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          {t('sidebar.renameDialog.title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            // The dialog exists solely to edit this field — focusing it on open
            // is the expected behaviour for an edit dialog (WAI-ARIA practice).
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmRename();
              }
            }}
            label={t('sidebar.renameDialog.label')}
            slotProps={{ htmlInput: { maxLength: 100 }, inputLabel: { shrink: true } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRenameTarget(null)}
            disabled={renameMutation.isPending}
            sx={{ color: 'text.secondary' }}
          >
            {t('sidebar.renameDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmRename}
            disabled={renameMutation.isPending || !renameValue.trim()}
            variant="contained"
          >
            {t('sidebar.renameDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <GuestAuthDialog open={guestAuthOpen} onClose={() => setGuestAuthOpen(false)} />
    </>
  );
}

// ===========================================================================
// helpers + small icons (chat-only — kept local to keep shared icons lean)
// ===========================================================================

type HistoryGroup = 'today' | 'yesterday' | 'lastWeek' | 'earlier';
const HISTORY_GROUPS: readonly HistoryGroup[] = ['today', 'yesterday', 'lastWeek', 'earlier'];

function bucketForTimestamp(iso: string | null): HistoryGroup {
  if (!iso) return 'earlier';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'earlier';
  const ageHours = (Date.now() - ts) / 36e5;
  if (ageHours < 24) return 'today';
  if (ageHours < 48) return 'yesterday';
  if (ageHours < 24 * 7) return 'lastWeek';
  return 'earlier';
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

interface GemRowProps {
  name: string;
  /** Optional custom glyph — API gems have none, so the initial stands in. */
  glyph?: ReactNode;
  accent: string;
  active: boolean;
  onClick: () => void;
}

function GemRow({ name, glyph, accent, active, onClick }: GemRowProps) {
  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      alignItems="center"
      spacing={1.25}
      onClick={onClick}
      sx={{
        appearance: 'none',
        border: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'start',
        cursor: 'pointer',
        boxSizing: 'border-box',
        width: '100%',
        px: 1.25,
        py: 0.9,
        borderRadius: (t) => `${t.shape.borderRadius}px`,
        backgroundColor: active ? `${accent}1F` : 'transparent',
        transition: 'background-color 0.15s',
        '&:hover': { backgroundColor: active ? `${accent}26` : 'action.hover' },
      }}
    >
      <GemBadge name={name} accent={accent} active={active} glyph={glyph} />
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          color: active ? 'text.primary' : 'text.secondary',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          flex: 1,
          textAlign: 'start',
        }}
      >
        {name}
      </Typography>
    </Stack>
  );
}
